/**
 * Generates an array of page numbers for pagination display.
 * Shows at most 7 entries, always includes first and last page,
 * and shows pages around the current page with ellipsis (-1) where needed.
 *
 * @param currentPage - The currently selected page (1-based)
 * @param totalPages - Total number of pages available
 * @returns Array of page numbers to display, with -1 representing ellipsis
 */
export function generatePaginationNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 1) {
    return totalPages === 1 ? [1] : []
  }

  if (totalPages <= 7) {
    // If we have 7 or fewer pages, show them all
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: number[] = []

  // Always add first page
  pages.push(1)

  // Calculate the optimal range around current page
  // We have 5 slots remaining after first and last page
  let start: number
  let end: number

  if (currentPage <= 4) {
    // Current page is near the beginning
    start = 2
    end = 5
    pages.push(2, 3, 4, 5)
    pages.push(-1) // ellipsis before last page
  } else if (currentPage >= totalPages - 3) {
    // Current page is near the end
    pages.push(-1) // ellipsis after first page
    start = totalPages - 4
    end = totalPages - 1
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
  } else {
    // Current page is in the middle
    pages.push(-1) // ellipsis after first page
    start = currentPage - 1
    end = currentPage + 1
    pages.push(start, currentPage, end)
    pages.push(-1) // ellipsis before last page
  }

  // Always add last page
  pages.push(totalPages)

  return pages
}
